import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Router from 'next/router';
import { useTranslations, useLocale } from 'next-intl';
import { parse } from 'url';

import { buildFilterQuery } from '../../../helper/api';

import useWindowEvent from '../../../hooks/useWindowEvent';

import { AngleDown } from '../../icons/arrow/arrow';
import CloseIcon from '../../icons/close/close';
import Container from '../../layout/container/container';
import Copy from '../../atoms/copy/copy';
import StyledTagFilter from './tagFilter.style';

const TagFilter = ({
  slug,
  updateTagValue,
  tags,
  topPadding = false,
  isLoading,
  filteredTags,
  initFilteredTags,
  resetFilteredTags
}) => {
  const t = useTranslations();
  const locale = useLocale();
  const tagList = useRef(null);
  const [isHidden, setIsHidden] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const customHistory = typeof window !== 'undefined' ? [window.location.href] : null;

  const handleResize = () => {
    if (window.innerWidth <= 768) {
      const { height } = tagList.current.getBoundingClientRect();

      setIsHidden(height > 140);
    } else if (window.innerWidth > 768 && isHidden) {
      setIsHidden(false);
    }
  };

  const handleRouteChange = tagArray => {
    // Page should always be 1 to avoid filtering on higher pages resulting in no results.
    const filterQuery = buildFilterQuery(tagArray, 1, 'tags');

    Router.replace(
      {
        pathname: `/${locale}/overview/blog`,
        query: { tags: tagArray, page: 1 }
      },
      `/${locale}/${slug}${filterQuery}`,
      { shallow: true }
    );
  };

  const updateFilter = event => {
    const { page } = Router.query;
    const { value, checked } = event.target;

    updateTagValue(value, checked, parseInt(page, 10) === 1);

    const newTagArray = checked
      ? [value, ...filteredTags]
      : filteredTags.filter(item => item !== value);

    handleRouteChange(newTagArray);
  };

  const resetFilter = () => {
    if (parse(window.location.href).pathname !== parse(customHistory[0]).pathname) {
      resetFilteredTags();
    }
    customHistory.unshift(window.location.href);
  };

  const resetAllTags = () => {
    resetFilteredTags(true);
    handleRouteChange(null);
  };

  useEffect(() => {
    const initialFilterValues = tags.filter(tag => tag.active === true).map(tag => tag.slug);

    if (initialFilterValues && initialFilterValues.length > 0) {
      initFilteredTags(initialFilterValues);
    }

    handleResize();

    return () => {
      resetFilter();
    };
  }, []);

  useWindowEvent('resize', () => handleResize());

  return (
    tags &&
    tags.length && (
      <StyledTagFilter isLoading={isLoading} isOpen={isOpen} topPadding={topPadding}>
        <Container>
          <div className="tagFilter__wrapper">
            {filteredTags.length ? (
              <button type="button" onClick={() => resetAllTags()} className="tagFilter__reset">
                <Copy tag="div" smallThick noMargin>
                  {t('tagsFilter.reset')}
                </Copy>
              </button>
            ) : (
              <Copy smallThick noMargin>
                {t('tagsFilter.headline')}
              </Copy>
            )}
            <div
              className={`tagFilter__list ${
                isHidden && !isOpen ? 'tagFilter__list--isHidden' : ''
              } ${isOpen ? 'tagFilter__list--isOpen' : ''}`}
              role="group"
              ref={tagList}
            >
              {tags.map(tag => (
                <div className="tagFilter__item" key={tag.slug}>
                  <input
                    type="checkbox"
                    id={tag.slug}
                    name="tagFilter"
                    value={tag.slug}
                    onChange={e => updateFilter(e)}
                    checked={filteredTags.indexOf(tag.slug) === -1 ? '' : true}
                    aria-checked={filteredTags.indexOf(tag.slug) === -1 ? 'false' : 'true'}
                    disabled={isLoading}
                    aria-disabled={isLoading}
                  />
                  <label htmlFor={tag.slug} className="tagFilter__name">
                    {tag.title}
                    {filteredTags.indexOf(tag.slug) !== -1 && <CloseIcon />}
                  </label>
                </div>
              ))}
            </div>

            {isHidden && (
              <button
                type="button"
                onClick={() => setIsOpen(prev => !prev)}
                className="tagFilter__expand"
              >
                {t(isOpen ? 'tagsFilter.collapse' : 'tagsFilter.expand')}
                <AngleDown />
              </button>
            )}
          </div>
        </Container>
      </StyledTagFilter>
    )
  );
};

TagFilter.propTypes = {
  topPadding: PropTypes.bool,
  slug: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  initFilteredTags: PropTypes.func.isRequired,
  resetFilteredTags: PropTypes.func.isRequired,
  updateTagValue: PropTypes.func.isRequired,
  tags: PropTypes.arrayOf(PropTypes.shape({})),
  filteredTags: PropTypes.arrayOf(PropTypes.string)
};

export default TagFilter;
